import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Search } from 'lucide-react';
import clsx from 'clsx';
import { useBloquear, useCuentas, type Cuenta, type EstadoCuenta } from '../api/cuentas';
import { mensajeDe } from '../api/equipo';
import { AyudaGuia, Pregunta } from '../components/Ayuda';

// Las cuentas que la gente crea en habitania.info, con su ficha del CRM y lo
// que ha hecho en su área privada. Solo el admin: ver y bloquear. Las cuentas
// del equipo se gestionan en Ajustes → Tu equipo.

const ESTADO: Record<EstadoCuenta, { texto: string; clase: string }> = {
  activa: { texto: 'Activa', clase: 'bg-leaf/50 text-deep' },
  sin_verificar: { texto: 'Sin verificar', clase: 'bg-amber-100 text-amber-800' },
  bloqueada: { texto: 'Bloqueada', clase: 'bg-red-100 text-red-700' },
};

const fecha = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function CuentasView() {
  const { data, isLoading, isError, refetch } = useCuentas();
  const [q, setQ] = useState('');

  const visibles = useMemo(() => {
    const n = q.trim().toLowerCase();
    const items = data?.items ?? [];
    return n ? items.filter((c) => [c.nombre, c.email, c.telefono].some((v) => v?.toLowerCase().includes(n))) : items;
  }, [data, q]);

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-deep">Cuentas</h1>
        <AyudaGuia seccion="cuentas" />
      </div>
      <p className="mt-1 max-w-2xl text-sm text-ink/55">
        Las cuentas que la gente crea en la web para entrar en su área privada: propietarios,
        inquilinos, compradores e inversores. Tu equipo está en Ajustes.
      </p>
      <Pregunta titulo="¿Qué es una cuenta y qué es una ficha?">
        La cuenta es con lo que esa persona entra en su área privada de la web: su correo y su
        contraseña. La ficha es lo que tú sabes de ella en Clientes → Cartera. Al registrarse, las dos
        se unen solas: si ya tenías su ficha con ese correo, se queda con esa.
      </Pregunta>
      <Pregunta titulo="¿Qué pasa si bloqueo una cuenta?">
        Esa persona deja de poder entrar en su área privada, y si estaba dentro se le cierra la sesión
        en menos de una hora. Su ficha, sus viviendas, sus contratos y sus incidencias no se tocan. Lo
        puedes deshacer con «Desbloquear» cuando quieras.
      </Pregunta>
      <Pregunta titulo="¿Cómo se da de baja a alguien?">
        Lo hace la propia persona desde su área privada (es su derecho, por la protección de datos):
        se borra su cuenta y su ficha queda anónima, porque sus contratos y facturas se conservan por
        ley. Si te lo pide a ti y no sabe hacerlo, díselo a Adrien.
      </Pregunta>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError || !data ? (
        <div className="mt-8 text-center">
          <p className="text-sm font-bold text-red-700">No se han podido cargar las cuentas</p>
          <button onClick={() => refetch()} className="mt-3 rounded-full bg-red-600 px-4 py-1.5 text-sm font-bold text-white">Reintentar</button>
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi label="Cuentas activas" value={data.resumen.activas} />
            <Kpi label="Altas en 30 días" value={data.resumen.altas_30_dias} />
            <Kpi label="Sin verificar" value={data.resumen.sin_verificar} />
            <Kpi label="Bloqueadas" value={data.resumen.bloqueadas} />
          </div>

          <div className="mt-5 flex items-center gap-2 rounded-full border border-deep/15 bg-surface px-4 py-2">
            <Search size={15} className="text-ink/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, correo o teléfono"
              className="w-full bg-transparent text-sm outline-none placeholder:text-ink/35" />
          </div>

          {visibles.length === 0 ? (
            <p className="mt-6 rounded-2xl border border-dashed border-deep/20 px-6 py-10 text-center text-sm text-ink/55">
              {q ? 'Ninguna cuenta con esa búsqueda.' : 'Todavía nadie ha creado una cuenta en la web.'}
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {visibles.map((c) => <FilaCuenta key={c.username} c={c} />)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function FilaCuenta({ c }: { c: Cuenta }) {
  const bloquear = useBloquear();
  const e = ESTADO[c.estado];
  const hecho = [
    [c.actividad.viviendas, 'vivienda', 'viviendas'],
    [c.actividad.contratos, 'contrato', 'contratos'],
    [c.actividad.incidencias, 'incidencia', 'incidencias'],
    [c.actividad.facturas, 'estudio de factura', 'estudios de factura'],
  ].filter(([n]) => (n as number) > 0).map(([n, uno, varios]) => `${n} ${n === 1 ? uno : varios}`);
  const error = mensajeDe(bloquear.error);

  return (
    <li className={clsx('rounded-2xl border bg-surface px-4 py-3', c.estado === 'bloqueada' ? 'border-red-200' : 'border-deep/10')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-deep">
            {c.nombre || c.email}
            {c.equipo && <span className="ml-2 rounded-full bg-deep/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-deep">Equipo</span>}
          </p>
          <p className="truncate text-xs text-ink/55">
            {[c.nombre ? c.email : '', c.telefono, `alta ${fecha(c.alta)}`].filter(Boolean).join(' · ')}
          </p>
          <p className="mt-1 text-xs text-ink/60">
            {c.perfiles.length > 0 && <span className="font-semibold capitalize">{c.perfiles.join(', ')} · </span>}
            {hecho.length ? hecho.join(' · ') : 'Todavía no ha hecho nada en su área'}
          </p>
        </div>
        <span className={clsx('rounded-full px-2.5 py-0.5 text-xs font-bold', e.clase)}>{e.texto}</span>
        {c.client_id && (
          <Link to={`/clientes?ficha=${encodeURIComponent(c.client_id)}`}
            className="rounded-full border border-deep/15 px-3 py-1 text-xs font-bold text-ink/65 hover:border-brand hover:text-brand">
            Ver ficha
          </Link>
        )}
        {!c.equipo && !c.soy_yo && (c.estado === 'bloqueada' ? (
          <button
            onClick={() => bloquear.mutate({ username: c.username, bloqueada: false })}
            disabled={bloquear.isPending}
            className="rounded-full bg-deep px-3 py-1 text-xs font-bold text-white hover:bg-brand disabled:opacity-50"
          >
            Desbloquear
          </button>
        ) : (
          <button
            onClick={() => {
              if (window.confirm(`¿Bloquear la cuenta de ${c.email}? No podrá entrar en su área privada hasta que la desbloquees.`))
                bloquear.mutate({ username: c.username, bloqueada: true });
            }}
            disabled={bloquear.isPending}
            className="rounded-full px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            Bloquear
          </button>
        ))}
      </div>
      {error && <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600"><AlertTriangle size={12} /> {error}</p>}
    </li>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-deep/10 bg-surface px-4 py-3">
      <p className="text-2xl font-extrabold text-deep">{value}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
    </div>
  );
}
